/**
 * BoardSurface — THE shared board-center surface for every workbench host.
 *
 * One component owns everything that renders "the board and its channel
 * chrome": the HUD chip row (edit toggle, view builder/persist/health, knobs,
 * aspects, plugin header items, plane switcher, hand-placement reset), the
 * on-board process step scrubber, the representation switch (board / table /
 * matrix / grid / index / flow — draft-view aware, so the WYSIWYG builder
 * previews live), the fully-wired BoardPane (rooted tabs, per-tab viewport
 * persistence, structural editing, debug overlay), and the color legend.
 *
 * Both the viewer (`ViewerComposition`) and Studio (`StudioComposition`) mount
 * THIS component instead of assembling their own copies — the drift class where
 * Studio silently lacked viewer features (edit mode, live builder preview,
 * rooted boards, placement reset…) dies here. Host-specific bits enter through
 * props (mobile chrome, boot viewport, extra HUD items); everything else
 * self-sources from the shared providers + the controller bag.
 */
import {
  useEffect,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type ReactNode,
  type SetStateAction,
} from "react";
import type { Viewport } from "@archlang/scene/board";
import type { ArchitectureViewHandle } from "../../architecture-view/ArchitectureView.js";
import type { HoverStore } from "../../board/hoverStore.js";
import type { NodeColoring } from "../../board/coloring/index.js";
import type { usePlaneSwitcher } from "../../hooks/usePlaneSwitcher.js";
import { useTranslation } from "../../i18n";
import { BoardPane } from "./BoardPane";
import { EditToggle } from "./EditToggle";
import { useOptionalEditMode } from "../EditModeContext";
import { StepScrubber } from "../../process/StepScrubber.js";
import { ColorLegend } from "../../board/coloring/index.js";
import { LifecycleScrubber } from "../../board/lifecycle/LifecycleScrubber.js";
import type { LifecycleWindow, Timeline } from "@archlang/engine";
import { PlaneSwitcher } from "../../board/PlaneSwitcher";
import { HudChip } from "../../components/HudChip";
import { DebugLayoutHud } from "../../board/hud/DebugLayoutHud";
import { AspectControls } from "./AspectControls";
import { ViewHealthBadge } from "../../views/ViewHealthBadge";
import { ViewBuilder } from "../../views/ViewBuilder";
import { ViewReprChrome } from "../../views/ViewReprChrome";
import { ViewExportProvider } from "../../views/ViewExportProvider";
import { ViewStatePersist } from "../../views/ViewStatePersist";
import { KnobPicker } from "../../views/KnobPicker";
import { ViewTableContainer } from "../../views/ViewTableContainer";
import { FlowContainer } from "../../views/FlowContainer";
import { ViewMatrixContainer } from "../../views/ViewMatrixContainer";
import { ViewGridContainer } from "../../views/ViewGridContainer";
import { ViewIndexContainer } from "../../views/ViewIndexContainer";
import { activeViewRepresentation, boardScrubberVisible, findView } from "../../views/viewRepr";
import { useEffectiveViewName } from "../../views/useEffectiveViewName";
import { useActiveView } from "../ActiveViewContext";
import { useHeaderItemContributions } from "../plugins/PluginRegistryContext.js";
import { type OverflowItem } from "../../components/OverflowToolbar";
import { useProcessPlaybackContext } from "../ProcessPlaybackContext.js";
import { useWorkspace } from "../WorkspaceContext.js";
import { useWorkspaceModel } from "../WorkspaceModelContext.js";
import { useActiveTabId, useTabs } from "../WorkspaceStore";
import { pickInitialViewport } from "./../perTabViewport";
import { IS_DEV } from "../isDev";

/**
 * The slice of the host's controller bag this surface consumes. Deliberately a
 * LOCAL structural contract — primitives must never import the composition
 * controller (see the primitive-self-sourcing boundary test); both the shared
 * workbench controller bag and the viewer's wrapper bag satisfy it structurally.
 */
export interface WorkbenchController {
  readonly boardRef: MutableRefObject<ArchitectureViewHandle | null>;
  readonly plane: ReturnType<typeof usePlaneSwitcher>;
  readonly placements: { readonly size: number };
  readonly clearPlacements: () => void;
  readonly debugLayout: boolean;
  readonly setDebugLayout: Dispatch<SetStateAction<boolean>>;
  readonly recomputeLayout: () => void;
  readonly animateRecompute: () => void | Promise<unknown>;
  readonly animating: boolean;
  readonly animPaused: boolean;
  readonly toggleAnimPause: () => void;
  readonly animStep: () => void;
  readonly refresh: () => void | Promise<unknown>;
  readonly nodeColoring: NodeColoring | null;
  /** Scrub-independent lifecycle windows off the projection plan. Empty (or
   *  absent) ⇒ the model carries no lifecycle decoration and the scrubber does
   *  not render at all — which is why every existing board is untouched. */
  readonly lifecycleWindows?: ReadonlyMap<string, LifecycleWindow>;
  /** The model's derived marker axis — the scrubber's ticks and the default
   *  scrub point (`now`) both come off it. */
  readonly timeline?: Timeline;
  readonly legendPinnedValue: string | null;
  readonly setLegendHoverValue: (value: string | null) => void;
  readonly setLegendPinnedValue: Dispatch<SetStateAction<string | null>>;
  readonly boardHoverStore: HoverStore | null | undefined;
  readonly handleBoardItemOpen: (id: string) => void;
  readonly handleViewportChange: (vp: Viewport) => void;
  readonly activeProcessId: string | null;
}

export interface BoardSurfaceProps {
  /** The host's controller bag (the shared workbench controller or its viewer
   *  wrapper) — see {@link WorkbenchController} for the consumed slice. */
  readonly c: WorkbenchController;
  /** Whether editing is possible at all in this host (LSP worker present,
   *  not mobile). Gates the edit toggle + builder/persist chrome. */
  readonly canEdit: boolean;
  /**
   * Offer the view builder in its EPHEMERAL tier alone — reconfigure the board
   * live, with no way to write the config back to `.arch`.
   *
   * The builder was already two-tier (reconfigure live; save when it earns its
   * keep), but both tiers hung off {@link canEdit}, so a host that cannot write
   * lost the reasoning tool along with the saving. They are different
   * capabilities: composing a view to think with costs nothing and touches
   * nothing. Implied by `canEdit`, which offers both tiers.
   */
  readonly canBuildViews?: boolean;
  /** Offer the camera operator ("smart view") toggle — meaningful only when an
   *  editor surface sits beside the board. */
  readonly operator: boolean;
  /** Phone layout (viewer only — Studio is desktop-only). */
  readonly isMobile?: boolean;
  /** Boot-restored viewport override for a board tab with no own camera yet
   *  (the viewer's share-link/persisted state). */
  readonly initialViewport?: Viewport;
  /** Per-tab viewport key fallback when no tab is active (the viewer keys the
   *  board on the source label pre-tabs). */
  readonly viewportKeyFallback?: string;
  /** Extra HUD chips appended after the aspect controls (host-specific). */
  readonly extraHudItems?: readonly OverflowItem[];
  /** Phone board-header search trigger. */
  readonly onMobileSearch?: () => void;
  /** Phone settings menu (render prop; `close` dismisses on a pick). */
  readonly mobileMenu?: (close: () => void) => ReactNode;
}

export function BoardSurface({
  c,
  canEdit,
  canBuildViews = false,
  operator,
  isMobile = false,
  initialViewport,
  viewportKeyFallback,
  extraHudItems,
  onMobileSearch,
  mobileMenu,
}: BoardSurfaceProps): ReactNode {
  const { t } = useTranslation("workbench");
  const store = useWorkspace();
  const tabs = useTabs(store);
  const activeTabId = useActiveTabId(store);
  const activeTab = tabs.find((tab) => tab.id === activeTabId);
  const { resolvedModel } = useWorkspaceModel();
  const { activePlane, setActivePlane, draftView } = useActiveView();
  const {
    walkResult,
    activeStepIndex,
    autoPlay,
    setAutoPlay,
    setActiveStepIndex,
    handleSelectProcess,
    handleLocateStep,
    gateAdvance,
    pendingChoice,
    choosePath,
    dismissChoice,
  } = useProcessPlaybackContext();
  // Per-view edit stance: WYSIWYG affordances (board structural edits, view
  // cell/scaffold edits) only arm when the active view is toggled into edit
  // mode. Viewing keeps clicks navigate/select even when edits are possible.
  const editMode = useOptionalEditMode();
  const editableNow = canEdit && editMode;
  // Which surface the active view renders as — `draftView` (the live builder
  // preview) wins over the named/tab view, same precedence the projection uses.
  const effectiveViewName = useEffectiveViewName();
  const activeViewRepr = activeViewRepresentation(
    draftView,
    effectiveViewName,
    resolvedModel?.views,
  );
  // Plugin-contributed header items (e.g. the OTLP telemetry "Live" toggle) —
  // merged into the HUD row so a plugin owns its own board-header control.
  const pluginHeaderItems = useHeaderItemContributions();

  // Board HUD action buttons, as an array so the OverflowToolbar can collapse
  // the rightmost ones into a "=" dropdown when the breadcrumb needs the room.
  // Order (leftmost survives overflow longest): edit toggle → view channel →
  // knobs → aspects → host/plugin extras → plane → layout reset.
  const hudItems: OverflowItem[] = [];
  // The per-view view↔edit toggle — the universal stance switch. Leftmost so it
  // survives overflow (the primary control); only renders when edits are possible.
  if (canEdit) hudItems.push({ key: "edit-mode", node: <EditToggle canEdit={canEdit} /> });
  // View WYSIWYG builder — compose a draft view in-browser (ephemeral tier),
  // save it into the model text when it earns its keep. Editing surfaces only.
  // ViewHealthBadge and ViewStatePersist are co-located in the same hudItem:
  // both render null in the common case (no diagnostics / no dirty pins) and an
  // empty wrapper div still consumes flex gap even when its child returns null.
  // ViewStatePersist writes dragged placements + knob values back to `.arch`,
  // so it belongs to the writing tier only — an ephemeral builder offering it
  // would present a button that cannot do what it says.
  if (canEdit) hudItems.push({ key: "view-build", node: <><ViewHealthBadge /><ViewStatePersist /><ViewBuilder /></> });
  else if (canBuildViews) hudItems.push({ key: "view-build", node: <><ViewHealthBadge /><ViewBuilder canSave={false} /></> });
  else hudItems.push({ key: "view-health", node: <ViewHealthBadge /> });
  // KnobPicker renders null when the effective view has no knobs — gate the
  // push so an unconditional empty wrapper doesn't create a phantom gap before
  // Aspects. Same resolution as the picker itself: draft > effective name
  // (qualified-first) — the raw HUD pick alone missed view TABS entirely.
  const effectiveView = draftView ?? findView(resolvedModel?.views, effectiveViewName);
  if (effectiveView?.header.knobs?.length) hudItems.push({ key: "knobs", node: <KnobPicker /> });
  hudItems.push({ key: "aspect", node: <AspectControls variant="hud" /> });
  hudItems.push(
    ...pluginHeaderItems.map((h) => ({
      key: `${h.pluginId}:${h.contribution.id}`,
      node: h.contribution.node,
    })),
  );
  if (extraHudItems) hudItems.push(...extraHudItems);
  if (c.plane.planes.length > 0) {
    hudItems.push({
      key: "plane",
      node: (
        <PlaneSwitcher planes={c.plane.planes} selected={activePlane} onSelect={setActivePlane} />
      ),
    });
  }
  if (c.placements.size > 0) {
    hudItems.push({
      key: "reset",
      node: (
        <HudChip onClick={c.clearPlacements} title={t("clearHandPlacements")}>
          {t("resetLayoutCount", { count: c.placements.size })}
        </HudChip>
      ),
    });
  }
  // The DEV layout debug controls render as tiny chips docked on the minimap
  // via `minimapExtras`, so debug reads visually distinct from primary
  // controls. Desktop only; the phone chrome keeps its own Debug menu section.
  const debugLayoutChips =
    IS_DEV && !isMobile ? (
      <DebugLayoutHud
        onRecompute={c.recomputeLayout}
        onAnimate={c.animateRecompute}
        animating={c.animating}
        animPaused={c.animPaused}
        onToggleAnimPause={c.toggleAnimPause}
        onStep={c.animStep}
        debugLayout={c.debugLayout}
        onToggleDebug={() => c.setDebugLayout((v) => !v)}
        onRefresh={c.refresh}
      />
    ) : undefined;

  // The board surface backs board AND file/doc tabs; a rooted board tab pins
  // its package root. Per-tab camera: key the single board on the active tab so
  // each tab keeps its own pan/zoom, restored from the tab's stored viewport.
  const boardRootPath =
    activeTab?.kind === "board" ? (activeTab.state.rootPath ?? undefined) : undefined;
  const boardViewportKey = activeTab ? activeTab.id : viewportKeyFallback;
  const perTabInitialViewport = pickInitialViewport(activeTab, initialViewport);

  // A freshly-opened rooted board tab (cmd-click) frames its entity once — its
  // own viewport then takes over (persisted per tab). Only fly when the tab has
  // no remembered camera yet, and at most once per tab id.
  const flownRef = useRef<string | null>(null);
  // Tab ids are deterministic (`board:<rootId>`), so a close + reopen of the
  // SAME entity would otherwise be mistaken for "already flown" and keep the
  // stale camera — forget the guard once its tab is gone.
  useEffect(() => {
    if (flownRef.current && !tabs.some((t) => t.id === flownRef.current)) {
      flownRef.current = null;
    }
  }, [tabs]);
  useEffect(() => {
    if (
      activeTab?.kind === "board" &&
      activeTab.state.rootId &&
      !activeTab.state.viewport &&
      flownRef.current !== activeTab.id
    ) {
      flownRef.current = activeTab.id;
      // A space roots INTO its contents (focal = the space); a leaf just frames.
      if (activeTab.state.rootIsSpace) c.boardRef.current?.enterSpace(activeTab.state.rootId);
      else c.boardRef.current?.flyTo(activeTab.state.rootId);
    }
  }, [activeTab, c.boardRef]);

  return (
    <>
      {walkResult && boardScrubberVisible(activeViewRepr, !!c.activeProcessId) && (
        <div
          data-hud
          // Phone: a bottom-centre transport bar (thumb territory; the top
          // corners are taken by the legend and minimap). Desktop: the usual
          // top-right HUD slot.
          className={
            "absolute z-hud pointer-events-auto " +
            (isMobile ? "inset-x-3 bottom-3 flex justify-center" : "right-3 top-14")
          }
        >
          <StepScrubber
            walkResult={walkResult}
            stepIndex={activeStepIndex}
            setStepIndex={setActiveStepIndex}
            autoPlay={autoPlay}
            setAutoPlay={setAutoPlay}
            onClose={() => handleSelectProcess(null)}
            mobile={isMobile}
            onLocateStep={handleLocateStep}
            onGateAdvance={gateAdvance}
            pendingChoice={pendingChoice}
            onChoosePath={choosePath}
            onDismissChoice={dismissChoice}
          />
        </div>
      )}

      {activeViewRepr !== "board" ? (
        // The provider lets a headerless tabular container publish its CSV
        // export up to ViewReprChrome (siblings sharing one context).
        <ViewExportProvider>
          <div className="flex h-full min-h-0 flex-col">
            {/* Non-board representation surfaces (table/matrix/grid/flow) don't
            mount the board's BoardChrome, so they carry a thin right-aligned
            bar with the view-channel controls (builder / aspect / health).
            No view dropdown — views are picked from the navigation panel. */}
            <ViewReprChrome canEdit={canEdit} />
            <div className="relative min-h-0 flex-1">
              {activeViewRepr === "table" ? (
                <ViewTableContainer editable={editableNow} />
              ) : activeViewRepr === "matrix" ? (
                <ViewMatrixContainer editable={editableNow} />
              ) : activeViewRepr === "grid" ? (
                <ViewGridContainer />
              ) : activeViewRepr === "index" ? (
                <ViewIndexContainer />
              ) : (
                <FlowContainer editable={editableNow} />
              )}
            </div>
          </div>
        </ViewExportProvider>
      ) : (
        <BoardPane
          handleRef={c.boardRef}
          operator={operator}
          editable={editableNow}
          // S16: a plane pick narrows the board to a SUBSET (via the
          // projection's `on <plane>`), replacing the v0.9 highlight/dim path.
          memberHighlightOverride={undefined}
          hoverStore={c.boardHoverStore ?? undefined}
          onItemOpen={c.handleBoardItemOpen}
          onItemOpenRooted={(id, isSpace) =>
            store.openTab({ kind: "board", rootId: id, rootIsSpace: isSpace })
          }
          chromeTrailingItems={hudItems}
          minimapExtras={debugLayoutChips}
          rootPath={boardRootPath}
          viewportKey={boardViewportKey}
          initialViewport={perTabInitialViewport}
          onViewportChange={(vp) => {
            c.handleViewportChange(vp);
            // Remember this tab's camera so a tab switch restores it.
            if (activeTab) store.updateTabState(activeTab.id, { viewport: vp });
          }}
          debugLayout={c.debugLayout}
          mobile={isMobile}
          onMobileSearch={onMobileSearch}
          mobileMenu={mobileMenu}
        />
      )}

      {/* The node-coloring legend is a BOARD affordance — it names the board's
      per-node color cast. A representation view (table/matrix/grid/flow)
      replaces the board and carries its own legend where relevant (the grid's
      color-getter legend), so the board legend would only double up and
      overlap. Gate it to the board. */}
      {/* Timeline scrubber — bottom-centre, the placement DESIGN-SYSTEM §2
      already reserves for a scrubber. Board-only for the same reason the
      legend is: a representation view replaces the board. */}
      {activeViewRepr === "board" && c.lifecycleWindows && c.lifecycleWindows.size > 0 && (
        <LifecycleScrubber
          windows={c.lifecycleWindows}
          timeline={c.timeline}
          mobile={isMobile}
        />
      )}

      {c.nodeColoring && activeViewRepr === "board" && (
        <ColorLegend
          title={c.nodeColoring.title}
          entries={c.nodeColoring.legend}
          pinnedId={c.legendPinnedValue}
          onHover={c.setLegendHoverValue}
          onPin={(id) => c.setLegendPinnedValue((v) => (v === id ? null : id))}
          mobile={isMobile}
        />
      )}
    </>
  );
}
